/**
 * Browser-side reader for the chats guest session token -- the credential `conversations-guest-chat-handle` mints and
 * the chats worker's `resolveGuestCaller` verifies. The SSOT both the assistant's WS credential resolver and the
 * function-card HTTP callers read, so the storage key is named in exactly one place.
 *
 * Null is NOT an error channel: an authenticated visitor never has one, and a guest has none until the bootstrap
 * `guest-chat-create` returns it. The CALLER decides what null means -- the WS skips the connect, an HTTP caller sends
 * no `Authorization` header and lets the worker's gate answer.
 *
 * Pure leaf: `window` is a browser runtime global and the key is a const, so nothing is value-imported beyond
 * `@federa/const` (the `utils/` floor allows only const/types/zod).
 */

import { GUEST_SESSION_TOKEN_STORAGE_KEY } from '@federa/const/auth-session';
import { nonEmptyStringOrNull } from '@federa/const/string-guards';

declare const window: undefined | { localStorage: { getItem(key: string): string | null } };

export function readStoredGuestSessionToken(): string | null {
	if (typeof window === 'undefined') return null;
	try {
		return nonEmptyStringOrNull(window.localStorage.getItem(GUEST_SESSION_TOKEN_STORAGE_KEY));
	} catch {
		// Silent: localStorage throws in private-mode and sandboxed-iframe contexts, which are indistinguishable here
		// from "no token stored" -- both mean the caller has no guest credential to present.
		return null;
	}
}
